<template>
  <div class="audio-unlimited-form opc-zmaudio__form-item">
    <!--header-->
    <div class="audio-unlimited-form__header">
      <h1>{{ title }}</h1>
      <zm-icon data-testid="checkout-button-76" v-if="canRemove" @click="handleRemove" aria-label="Remove">
        <ManualCloseMark />
      </zm-icon>
    </div>
    <p class="audio-unlimited-form__sub-title" v-html="desc" />
    <div class="audio-unlimited-form__license-row">
      <InputNumberWithPopover
        :modelValue="unlimitedAudioLicense"
        controls-position="both"
        @update:modelValue="handleUnlimitedLicenseChange"
        :label="'Zoom Audio US & Canada Select the number of users'"
        :data-tracking="{ buyInteraction: 'Zoom Audio US & Canada' }"
        size="lg"
        :min="min"
        :max="max"
        :placement="'top'"
        :data-testid="`checkout-input-license-${value ? value.plan_code : ''}`"
      />
      <span class="license-span">{{ licenseText }}</span>
    </div>
    <zm-banner show-close v-if="showRequireNumberAlert" class="opc-common-info-alert" type="info" show-icon>
      <div
        data-testid="checkout-div-77"
        v-html="uscaLicenseMatchTips"
        @click="handleBannerLinkClick"
        @mouseover="handleBannerMouseover"
      ></div>
    </zm-banner>
    <div class="audio-unlimited-form__subscription-cycle">
      <h2 class="audio-unlimited-form__subscription-cycle-title">{{ $t('billing.select_billing_cycle') }}</h2>
      <cycle-radio-group
        class="audio-unlimited-form__radio-group opc-card-radio"
        :value="value_billingCycle"
        :model-value="value_billingCycle"
        :plans="audioUnlimitedPlans"
        :action="switchBillingCycle"
        aria-label="Zoom Audio Unlimited Billing Cycle">
        <zm-tooltip
          v-for="item in subscriptionCycleOptions"
          :key="item.label"
          :disabled="!billingCycleDisabled(item.label)"
          tabindex="-1">
          <template #content>
          <div v-html="billingCycleDisabled(item.label) || ''" />
          </template>
          <zm-radio
            :data-testid="`checkout-radio-billing-cycle-${item.planCode}`"
            :label="item.label" :disabled="!!billingCycleDisabled(item.label)"
            class="audio-unlimited-form__radio">
            <slot>
              <p class="audio-unlimited-form__radio-title">
                <span>{{ item.cycle }}</span>
              </p>
              <p class="subscription-cycle__radio-subtitle">
                <span class="audio-unlimited-form__radio-price opc-product-price-container" tabindex="-1" :aria-label="ariaPriceOfMonthForLabel({ amount: audioUnlimitedPlans.find(plan => plan.billing_cycle === item.label).amount, billing_cycle: item.label })">
                  <span v-for="(value, key, index) in item.priceObject"
                        :key="index"
                        :class="[`opc-product-price-container__${key}`, useSmallSize(value) ? 'opc-product-price-container__small' : '']"
                        :style="value.symbol && value.place === 'end' ? { order: 1 } : {}"
                        aria-hidden="true">
                    {{ value.symbol ? value.symbol : value }}
                  </span>
                </span>
              </p>
              <p class="subscription-cycle__radio-subtitle">
                <span class="audio-unlimited-form__radio-unit" v-html="item.unit"></span>
              </p>
              
            </slot>
          </zm-radio>
        </zm-tooltip>
      </cycle-radio-group>
    </div>
  </div>
</template>
<script>
import { planTypes, AUDIO_SUB_TYPES, BILLING_CYCLE, BillingCycleEnum, A_LINK_TAG_NAME } from '@/common/constants';
import computedMixins from '@/plans/TOLLFREE/mixins/computed-mixins';
import discountedMonthPriceMixin from '@/common/mixin/discounted-month-price';
import { compact, get } from 'lodash';
import { mapActions } from 'vuex';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import CycleRadioGroup from '@/components/service/cycle-radio-group';
import { isOnlineNotSupportPlan } from '@/common/service/non-standard-billing-cycle-utils';
import { ManualCloseMark } from '@zoom/icons-vue';
import { getPlanUnitByQuantity, planUnitMap } from '@/common/product-unit';
import { mapGetters } from 'vuex';

export default {
  name: 'AudioUnlimitedForm',
  mixins: [computedMixins, discountedMonthPriceMixin],
  components: {
    InputNumberWithPopover,
    CycleRadioGroup,
    ManualCloseMark,
  },
  props: {
    title: {
      type: String,
      require: true,
    },
    desc: {
      type: String,
      require: true,
    },
  },
  computed: {
    ...mapGetters(['maxCycleValueBasePlan', 'supportAutoAddAnotherBasePlan']),
    audioUnlimitedPlans() {
      if (isOnlineNotSupportPlan({ planType: planTypes.TOLLFREE, billingCycle: this.value?.billing_cycle, category: AUDIO_SUB_TYPES.UNLIMITED })) {
        return this.audioUnlimitedPlansFilter(this.planMapFlat).filter(item => item.billing_cycle === this.value?.billing_cycle);
      }
      return compact([
        this.audioUnlimitedPlansFilter(this.planMapFlat).find(item => item.billing_cycle === BILLING_CYCLE.ANNUAL),
        this.audioUnlimitedPlansFilter(this.planMapFlat).find(item => item.billing_cycle === BILLING_CYCLE.MONTHLY),
      ])
    },
    subscriptionCycleOptions() {
      return this.audioUnlimitedPlans.map(item => ({
        label: item.billing_cycle,
        cycle: this.$t(`billing.cycle.${item.billing_cycle}`),
        priceObject: this.getPriceOfMonth(item.amount, item.billing_cycle, { forceDiff: true }),
        unit: this.getMergedBilledText(item.billing_cycle, item.amount),
        planCode: item.plan_code,
      }))
    },
    value() {
      return this.audioUnlimitedPlansFilter(this.getSelectedPlans)[0];
    },
    value_billingCycle() {
      return get(this.value, 'billing_cycle', '');
    },
    basePlanCycleValue() {
      return this.maxCycleValueBasePlan?.cycleValue || 0;
    },
    basePlanName() {
      return this.mx_basePlanName;
    },
    min() {
      if(
        this.mx_unlimitedRelatePlans.length === 1 &&
        (this.$context.is.buyNow || this.$context.is.basic2pro)
      ) {
        return this.mx_unlimitedRelatePlans[0].min || 1;
      };
      return this.unlimitedAudioLicense;
    },
    max() {
      if(
        this.mx_unlimitedRelatePlans.length === 1 &&
        (this.$context.is.buyNow || this.$context.is.basic2pro)
      ) {
        return this.mx_unlimitedRelatePlans[0].max || 99;
      };
      return this.unlimitedAudioLicense;
    },
    canRemove() {
      return this.mx_selectedCategories.length > 1 && this.mx_newCategorys.includes(AUDIO_SUB_TYPES.UNLIMITED);
    },
    showRequireNumberAlert({ $context }) {
      return ($context.is.add2cart || $context.is.edit) && this.min === this.max;
    },
    uscaLicenseMatchTips() {
      const hasZR = this.mx_unlimitedBoughtRelatePlanTypes.includes('zoomroom');
      if(hasZR) return this.$t('billing.tollfree.require_equal_number_for_audio_unlimited_alert.meeting_and_rooms');
      return this.$t('billing.tollfree.require_equal_number_for_audio_unlimited_alert.meeting_only')
    },
    licenseText() {
      return getPlanUnitByQuantity(planUnitMap.license, this.unlimitedAudioLicense);
    },
  },
  methods: {
    ...mapActions(['switchBillingCycleAction']),
    audioUnlimitedPlansFilter(data) {
      return data
      .filter(item => item.type === planTypes.TOLLFREE)
      .filter(item => item.category === AUDIO_SUB_TYPES.UNLIMITED);
    },
    async switchBillingCycle(oldPlan, newPlan) {
      if (!this.value) {
        return;
      }

      const cartInfo = { selected_number: this.value.cartInfo.selected_number };
      const nextPlan = await this.switchBillingCycleAction([oldPlan?.plan_code, {
        ...newPlan,
        cartInfo,
      }]);

      this.$emit('change', nextPlan)

      // event tracking
      const billingCycle = newPlan?.billing_cycle;
      billingCycle && this.$tracking.buyInteraction(
        `Zoom Audio US & Canada plan`,
        this.$tEn(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
      );
    },
    handleUnlimitedLicenseChange(value) {
      this.mx_handleUnlimitedLicenseClick();
      if (
        !this.mx_showUnlimited ||
        !this.mx_basePlan.plan_code
      ) return;

      const licenseChange = value - this.unlimitedAudioLicense;
      const oldLicense = this.mx_basePlan.cartInfo.selected_number;
      const nextLicese = oldLicense + licenseChange;
      this.updateSelectPlan({
        ...this.mx_basePlan,
        planType: planTypes.TOLLFREE,
        cartInfo: { selected_number: nextLicese },
      });
    },
    billingCycleDisabled(cycle) {
      if(BillingCycleEnum[cycle] > this.basePlanCycleValue && !this.supportAutoAddAnotherBasePlan) {
        return this.$t('billing.planform.annual_addon_disable_tooltip', [
          this.basePlanName,
          this.$t('billing.tollfree.audio_us_ca.title'),
          this.basePlanName,
        ]);
      }
      return false;
    },
    handleRemove() {
      this.$emit('remove', AUDIO_SUB_TYPES.UNLIMITED);
    },
    handleBannerLinkClick(event) {
      if (event?.target?.tagName === A_LINK_TAG_NAME) {
        const text = event?.currentTarget?.innerText
        this.$tracking.buyInteraction(
          'banner - product level',
          `audio conferencing - ${text}`);
      }
    },
    handleBannerMouseover(event) {
      const text = event?.currentTarget?.innerText
      this.$tracking.buyInteraction('banner - product level', `Audio Conferencing - ${text} - impression`);
    },
  }
}
</script>
<style lang="scss">
@import '@/styles/scss-utils.scss';

.audio-unlimited-form {
  &__header {
    display: flex;
    align-items: center;
    h1 {
      flex: 1;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      /* Title/Title S */
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%; /* 27px */
    }
    .zm-icon {
      font-size: 20px;
      cursor: pointer;
    }
  }
  &__desc {
    color: var(--neutral-neutral-08, #515079);

    /* body/LF — S (R) */
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 21px */

    margin-top: 8px;
  }
  &__license-row {
    display: flex;
    align-items: center;
    margin-top: 8px;
    .license-span {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

      /* Body/Body S */
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */
      margin-left: 8px;
    }
  }
  &__subscription-cycle {
    margin-top: 24px;
    &-title {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

      /* Title/Title S */
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 150%; /* 27px */
    }
  }

  &__radio-group {
    margin-top: 8px;
  }
  &__radio {
    &.zbo-radio {
      width: 100%;
      .zbo-radio__wrap {
        width: 100%;
      }
      .zbo-radio__label {
        width: 100%;
      }
      .zbo-radio__label--inner {
        width: 100%;
      }
    }
    &-title {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

      /* Label/Label L */
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 24px */
    }
    &-subtitle {
      vertical-align: baseline;
    }
    &-price {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

      /* Title/Title L */
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%; /* 33px */
    }
    &-unit {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }
  }
}
</style>
